import { render } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { inviteAnswers } from "@fobu/engine";
import { FormApp } from "./components/FormApp";
import { LazyPayment, MessageView } from "./components/EndingView";
import { LazyLive, LazyMailAnswer } from "./components/Common";
import { FormNotFoundError, HttpClient, InviteRequiredError, PasswordRequiredError, type PublishedForm } from "./lib/client";
import { PasswordGate } from "./components/PasswordGate";
import { setupEmbed } from "./lib/embed";
import { t } from "./lib/i18n";
import { mailFieldId, readUrlParams } from "./lib/prefill";
import "./styles.css";

/**
 * Routen:
 *   /f/<slug>        veröffentlichtes Formular (API)
 *   /demo/<slug>     Beispielformular, rein lokal
 *   /preview         Vorschau für den Editor (Definition per postMessage)
 *   /                Übersicht der Beispiele
 */
function Router() {
  const path = location.pathname.replace(/\/+$/, "") || "/";
  const demo = /^\/demo\/([a-z0-9-]+)$/.exec(path);
  const live = /^\/f\/([A-Za-z0-9_-]+)$/.exec(path);
  if (/^\/f\/[A-Za-z0-9_-]+\/verwalten$/.test(path)) return <LazyManage />;
  if (/^\/f\/[A-Za-z0-9_-]+\/rueckfrage$/.test(path)) return <LazyQuery />;
  if (/^\/f\/[A-Za-z0-9_-]+\/zahlung$/.test(path)) return <LazyPayment token={location.hash.slice(1)} standalone />;
  if (/^\/f\/[A-Za-z0-9_-]+\/ergebnisse$/.test(path)) return <LazyLive token={location.hash.slice(1)} lang={new URLSearchParams(location.search).get("lang") ?? navigator.language} standalone />;
  const results = /^\/r\/([A-Za-z0-9_-]{16,40})$/.exec(path);
  if (results) return <LazyResults token={results[1]!} />;
  if (demo) return <LazyLocal page="Demo" slug={demo[1]!} />;
  if (live) return <Live slug={live[1]!} />;
  if (path === "/preview") return <LazyLocal page="Preview" />;
  if (path === "/beispiele") return <LazyLocal page="Index" />;
  if (path === "/") return <HostRoot />;
  return <LazyLocal page="Index" />;
}

/** Auf einer eigenen Domain zeigt „/“ direkt das zugeordnete Formular, sonst die Beispielübersicht. */
function HostRoot() {
  const [slug, setSlug] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    fetch("/api/v1/public/by-host")
      .then(async (r) => (r.ok ? setSlug(((await r.json()) as { slug: string }).slug) : setSlug(null)))
      .catch(() => setSlug(null));
  }, []);
  if (slug === undefined) return <div class="fb-loading" aria-busy="true" />;
  return slug ? <Live slug={slug} /> : <LazyStart />;
}

function Live({ slug }: { slug: string }) {
  const [client] = useState(() => new HttpClient());
  const [state, setState] = useState<{ form?: PublishedForm; error?: "not_found" | "failed" | "invite"; locked?: PasswordRequiredError }>({});
  // Fortsetzungslink: ?r=… lädt den gespeicherten Zwischenstand.
  const [resumeToken] = useState(() => new URLSearchParams(location.search).get("r"));
  const [direct, setDirect] = useState(() => new URLSearchParams(location.search).get("fbd") === "1");
  const load = () =>
    client.load(slug, resumeToken).then(
      // Die API liefert die Definition bereits validiert und mit Defaults – kein zod im Browser.
      (form) => setState({ form }),
      (e) =>
        setState(
          e instanceof PasswordRequiredError ? { locked: e } : { error: e instanceof FormNotFoundError ? "not_found" : e instanceof InviteRequiredError ? "invite" : "failed" },
        ),
    );
  useEffect(() => {
    void load();
  }, [slug]);
  if (state.locked) {
    return (
      <PasswordGate
        title={state.locked.title}
        theme={state.locked.theme}
        onUnlock={async (pw) => {
          if (!(await client.unlock(slug, pw))) return false;
          await load();
          return true;
        }}
      />
    );
  }
  if (state.error === "not_found") return <MessageView title={t.notFoundTitle} body={t.notFoundBody} />;
  if (state.error === "invite") return <MessageView title={t.inviteOnlyTitle} body={t.inviteOnlyBody} />;
  if (state.error) return <MessageView title={t.loadFailed} />;
  if (!state.form) return <div class="fb-loading" aria-busy="true" />;
  const { form } = state;
  const { hidden, answers } = readUrlParams(form.definition, location.search);
  // „Sofort werten“: Klick aus der Mail speichert gleich (auch erneut, um die Antwort zu ändern).
  const params = new URLSearchParams(location.search);
  const mailKey = params.get("fbm");
  if (direct && form.invite && mailKey && params.get(mailKey) && params.get("i")) {
    return (
      <LazyMailAnswer
        def={form.definition}
        versionId={form.versionId}
        client={client}
        token={params.get("i")}
        fieldKey={mailKey}
        rawValue={params.get(mailKey)}
        initialAnswers={{ ...inviteAnswers(form.definition, form.invite), ...answers }}
        hidden={hidden}
        remaining={form.remaining}
        onNeedsForm={() => setDirect(false)}
      />
    );
  }
  if (form.invite?.completed) return <MessageView title={t.inviteUsedTitle} body={t.inviteUsedBody} />;
  if (form.invite) Object.assign(answers, { ...inviteAnswers(form.definition, form.invite), ...answers });
  const fromMail = mailFieldId(form.definition, location.search, answers);
  return (
    <FormApp
      def={form.definition}
      versionId={form.versionId}
      client={client}
      draftKey={slug}
      onceKey={slug}
      hidden={{ ...(form.resume?.hidden ?? {}), ...hidden }}
      initialAnswers={answers}
      {...(fromMail ? { fromMail } : {})}
      {...(form.resume && resumeToken ? { resume: { ...form.resume, token: resumeToken } } : {})}
      {...(form.remaining ? { remaining: form.remaining } : {})}
      {...(form.closed ? { closed: true } : {})}
    />
  );
}

/** Öffentliche Startseite (Formularübersicht): eigener Chunk – nicht Teil jedes Formulars. */
function lazyPage(load: () => Promise<() => preact.JSX.Element>) {
  return function Page() {
    const [View, setView] = useState<null | (() => preact.JSX.Element)>(null);
    useEffect(() => {
      void load().then((v) => setView(() => v));
    }, []);
    return View ? <View /> : <div class="fb-loading" aria-busy="true" />;
  };
}
/** Beispielseiten und Editor-Vorschau als eigener Teil. */
function LazyLocal({ page, slug }: { page: "Demo" | "Preview" | "Index"; slug?: string }) {
  const [mod, setMod] = useState<typeof import("./components/LocalPages") | null>(null);
  useEffect(() => {
    void import("./components/LocalPages").then(setMod);
  }, []);
  if (!mod) return <div class="fb-loading" aria-busy="true" />;
  const P = mod[page] as (p: { slug: string }) => preact.JSX.Element;
  return <P slug={slug ?? ""} />;
}
/** Selbstbearbeitung über den Verwaltungslink als eigener Teil. */
const LazyManage = lazyPage(() => import("./components/ManageView").then((m) => m.ManageView));
const LazyStart = lazyPage(() => import("./components/StartPage").then((m) => m.StartPage));

/** Rückfrage im Freigabeprozess als eigener Chunk. */
function LazyQuery() {
  const [View, setView] = useState<null | (() => preact.JSX.Element)>(null);
  useEffect(() => {
    void import("./components/QueryView").then((m) => setView(() => m.QueryView));
  }, []);
  return View ? <View /> : <div class="fb-loading" aria-busy="true" />;
}

/** Ergebnisseite als eigener Chunk – das Formular-Bundle bleibt klein. */
function LazyResults({ token }: { token: string }) {
  const [View, setView] = useState<null | ((p: { token: string }) => preact.JSX.Element)>(null);
  useEffect(() => {
    void import("./components/ResultsView").then((m) => setView(() => m.ResultsView));
  }, []);
  return View ? <View token={token} /> : <div class="fb-loading" aria-busy="true" />;
}

setupEmbed();
render(<Router />, document.getElementById("app")!);
